'use client'

import { useState, useEffect } from 'react'
import { ExternalLink, Image as ImageIcon, Video, FileText } from 'lucide-react'
import { panel } from './ui'

interface UrlPreviewProps {
  url: string
}

interface UrlMetadata {
  title?: string
  description?: string
  image?: string
  type: 'youtube' | 'vimeo' | 'image' | 'other'
}

export default function UrlPreview({ url }: UrlPreviewProps) {
  const [metadata, setMetadata] = useState<UrlMetadata | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    const fetchMetadata = async () => {
      if (!url || !url.trim()) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(false)

        // Check if it's a YouTube URL
        const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
        const youtubeMatch = url.match(youtubeRegex)
        if (youtubeMatch) {
          const videoId = youtubeMatch[1]
          // Try to get video title via our backend API (which will use oEmbed)
          try {
            const apiResponse = await fetch(`/api/brief613/url-metadata?url=${encodeURIComponent(url)}`)
            if (apiResponse.ok) {
              const apiData = await apiResponse.json()
              if (apiData.success && apiData.metadata) {
                setMetadata({
                  title: apiData.metadata.title || 'YouTube Video',
                  description: apiData.metadata.description || '',
                  image: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
                  type: 'youtube',
                })
                setLoading(false)
                return
              }
            }
          } catch (e) {
            // Fall through to default
          }
          // Default YouTube metadata
          setMetadata({
            title: 'YouTube Video',
            description: url,
            image: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
            type: 'youtube',
          })
          setLoading(false)
          return
        }

        // Check if it's a Vimeo URL
        const vimeoRegex = /vimeo\.com\/(\d+)/
        const vimeoMatch = url.match(vimeoRegex)
        if (vimeoMatch) {
          const videoId = vimeoMatch[1]
          try {
            const response = await fetch(`https://vimeo.com/api/v2/video/${videoId}.json`)
            if (response.ok) {
              const data = await response.json()
              if (data[0]) {
                setMetadata({
                  title: data[0].title || 'Vimeo Video',
                  description: data[0].description || '',
                  image: data[0].thumbnail_large || data[0].thumbnail_medium,
                  type: 'vimeo',
                })
                setLoading(false)
                return
              }
            }
          } catch (e) {
            // Fall through to default
          }
        }

        // Check if it's an image URL
        const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i
        if (imageExtensions.test(url)) {
          setMetadata({
            title: 'Image',
            description: url,
            image: url,
            type: 'image',
          })
          setLoading(false)
          return
        }

        // Try to fetch Open Graph metadata
        try {
          const response = await fetch(`/api/brief613/url-metadata?url=${encodeURIComponent(url)}`)
          if (response.ok) {
            const data = await response.json()
            if (data.success && data.metadata) {
              setMetadata({
                title: data.metadata.title || 'Link',
                description: data.metadata.description || '',
                image: data.metadata.image || data.metadata.ogImage,
                type: 'other',
              })
              setLoading(false)
              return
            }
          }
        } catch (e) {
          // Fall through to default
        }

        // Default: no metadata found
        setMetadata({
          title: 'Link',
          description: url,
          type: 'other',
        })
      } catch (err) {
        console.error('Error fetching URL metadata:', err)
        setError(true)
        setMetadata({
          title: 'Link',
          description: url,
          type: 'other',
        })
      } finally {
        setLoading(false)
      }
    }

    fetchMetadata()
  }, [url])

  if (loading) {
    return (
      <div className={`${panel} p-4 animate-pulse`}>
        <div className="flex items-center gap-3">
          <div className="w-16 h-16 bg-white/10 rounded-lg"></div>
          <div className="flex-1">
            <div className="h-4 bg-white/10 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-white/10 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !metadata) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-white/55 hover:text-white/85 text-sm p-2 rounded-lg hover:bg-white/5 transition-colors"
      >
        <ExternalLink size={16} />
        {url}
      </a>
    )
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`${panel} p-4 hover:border-white/20 transition-all block group`}
    >
      <div className="flex items-start gap-4">
        {metadata.image && (
          <div className="flex-shrink-0">
            <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-white/5">
              {metadata.type === 'youtube' || metadata.type === 'vimeo' ? (
                <>
                  <img
                    src={metadata.image}
                    alt={metadata.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <Video size={24} className="text-white/70" />
                  </div>
                </>
              ) : metadata.type === 'image' ? (
                <img
                  src={metadata.image}
                  alt={metadata.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.style.display = 'none'
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon size={24} className="text-white/40" />
                </div>
              )}
            </div>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {metadata.type === 'youtube' && <Video size={16} className="text-white/50" />}
            {metadata.type === 'vimeo' && <Video size={16} className="text-white/50" />}
            {metadata.type === 'image' && <ImageIcon size={16} className="text-white/50" />}
            {metadata.type === 'other' && <FileText size={16} className="text-white/50" />}
            <h4 className="text-sm font-semibold text-white/90 truncate group-hover:text-white">
              {metadata.title}
            </h4>
          </div>
          {metadata.description && (
            <p className="text-xs text-white/45 line-clamp-2 mb-2">{metadata.description}</p>
          )}
          <p className="text-xs text-white/40 truncate">{url}</p>
        </div>
        <ExternalLink size={16} className="text-white/40 group-hover:text-white/60 flex-shrink-0 mt-1" />
      </div>
    </a>
  )
}
